// src/components/CurrencysettingTabs.js
//
// Currency Settings page.
//   * BASE currency  = currency of the shop's country (fixed, prices are saved in it)
//   * DISPLAY currency = chosen here. Only currencies of countries that have a
//     shop are listed (never the whole world).
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './CurrencysettingTabs.css';
import axios from 'axios';
import { FaBox, FaSync } from 'react-icons/fa';
import Swal from 'sweetalert2';
import { HiCurrencyDollar } from 'react-icons/hi';
import { useCurrency } from '../../context/CurrencyContext';

const API_BASE_URL = 'https://super-backend.hira-tech.co.uk';

const formatTime = (date) => {
  if (!date) return null;
  try {
    return date.toLocaleString();
  } catch (e) {
    return null;
  }
};

const CurrencysettingTabs = () => {
  const {
    shopBaseCurrency,
    shopBaseSymbol,
    shopBaseName,
    displayCurrency,
    displaySymbol,
    displayName,
    getRateBetween,
    lastUpdated,
    loading: contextLoading,
    initialized,
    error: contextError,
    shopId: contextShopId,
    availableCurrencies,
    updateCurrencySettings,
    resetToShopCurrency,
    refreshSettings,
    clearError,
  } = useCurrency();

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [selectedShopId, setSelectedShopId] = useState(contextShopId || null);
  const [shops, setShops] = useState([]);
  const [selectedCode, setSelectedCode] = useState(displayCurrency);

  // ---- shop id (context first, URL as a backup) ----
  const getShopIdFromUrl = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const queryShopId = urlParams.get('shop_id');
    if (queryShopId) return queryShopId;
    const pathMatch = window.location.pathname.match(/\/shop\/(\d+)/);
    return pathMatch ? pathMatch[1] : null;
  }, []);

  useEffect(() => {
    if (contextShopId) {
      setSelectedShopId(contextShopId);
    } else if (!selectedShopId) {
      const fromUrl = getShopIdFromUrl();
      if (fromUrl) setSelectedShopId(fromUrl);
    }
  }, [contextShopId, selectedShopId, getShopIdFromUrl]);

  // ---- when the saved currency changes (shop switched / saved) the dropdown follows ----
  useEffect(() => {
    setSelectedCode(displayCurrency);
  }, [displayCurrency, selectedShopId]);

  // ---- shops (only for the shop name + country in the header) ----
  useEffect(() => {
    const fetchShops = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const response = await axios.get(`${API_BASE_URL}/user/shops/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = response.data;
        setShops(Array.isArray(data) ? data : data.results || data.shops || []);
      } catch (err) {
        // the page still works without the header details
      }
    };
    fetchShops();
  }, []);

  const currentShop = useMemo(
    () => shops.find((s) => String(s.shop_id || s.id) === String(selectedShopId)) || null,
    [shops, selectedShopId]
  );
  const currentShopName = currentShop ? currentShop.shop_name : `Shop #${selectedShopId}`;
  const currentShopCountry = currentShop ? currentShop.country : null;

  // ---- only currencies of countries that have a shop ----
  const currencies = availableCurrencies;
  const selectedCurrency =
    currencies.find((c) => c.code === selectedCode) ||
    currencies.find((c) => c.code === displayCurrency) ||
    currencies[0] || { code: shopBaseCurrency, symbol: shopBaseSymbol, name: shopBaseName, countries: [] };

  // ---- rate for what is selected right now (1 base = ? selected) ----
  const selectedRate = getRateBetween(shopBaseCurrency, selectedCurrency.code);
  const rateKnown = selectedRate > 0;
  const hasChanges = selectedCurrency.code !== displayCurrency;
  const isShowingBase = displayCurrency === shopBaseCurrency;
  const updatedAt = formatTime(lastUpdated);

  // ---- handlers ----
  const handleSelect = (currencyItem) => {
    setSelectedCode(currencyItem.code);
    setIsDropdownOpen(false);
  };

  const saveSettings = async () => {
    if (!rateKnown) return;
    setIsSaving(true);
    setError(null);

    try {
      const result = await updateCurrencySettings(selectedCurrency.code);
      if (!result.success) throw new Error(result.error);

      await Swal.fire({
        icon: 'success',
        title: 'Saved!',
        text: `Prices are now shown in ${selectedCurrency.name} (${selectedCurrency.code})`,
        timer: 2000,
        showConfirmButton: false,
        toast: true,
        position: 'top-end',
      });
    } catch (err) {
      await Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'Failed to save settings: ' + (err.message || 'Unknown error'),
        confirmButtonColor: '#004aad',
      });
      setError('Failed to save settings: ' + (err.message || 'Unknown error'));
    } finally {
      setIsSaving(false);
    }
  };

  const resetToDefault = async () => {
    const answer = await Swal.fire({
      title: 'Are you sure?',
      text: `Show prices in ${shopBaseName} (${shopBaseCurrency}) again?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#004aad',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, reset it!',
      cancelButtonText: 'Cancel',
    });
    if (!answer.isConfirmed) return;

    setIsSaving(true);
    setError(null);
    try {
      const result = await resetToShopCurrency();
      if (!result.success) throw new Error(result.error);

      setSelectedCode(shopBaseCurrency);
      await Swal.fire({
        icon: 'success',
        title: 'Reset!',
        text: `Back to ${shopBaseCurrency}`,
        timer: 1500,
        showConfirmButton: false,
        toast: true,
        position: 'top-end',
      });
    } catch (err) {
      await Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'Failed to reset: ' + (err.message || 'Unknown error'),
        confirmButtonColor: '#004aad',
      });
      setError('Failed to reset: ' + (err.message || 'Unknown error'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleRefresh = async () => {
    Swal.fire({
      title: 'Refreshing...',
      text: 'Updating exchange rates',
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    const result = await refreshSettings();
    Swal.close();

    if (result.success) {
      Swal.fire({
        icon: 'success',
        title: 'Refreshed!',
        text: 'Exchange rates are up to date.',
        timer: 1500,
        showConfirmButton: false,
        toast: true,
        position: 'top-end',
      });
    } else {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'Failed to refresh: ' + (result.error || 'Unknown error'),
        confirmButtonColor: '#004aad',
      });
    }
  };

  // ---- no shop open ----
  if (!selectedShopId) {
    return (
      <div className="currency-settings-wrapper">
        <div className="currency-header-section">
          <h2>Currency Settings</h2>
          <p>Manage your currency display settings for each shop</p>
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '80px 20px',
            textAlign: 'center',
          }}
        >
          <FaBox style={{ fontSize: '64px', color: '#004aad', marginBottom: '20px' }} />
          <h3 style={{ marginBottom: '10px', color: '#004aad' }}>No Shop Selected</h3>
          <p style={{ color: '#004aad', marginBottom: '20px' }}>
            Please select a shop to manage currency settings.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="currency-settings-wrapper">
      {/* Header: icon + title (left), Refresh button (right) */}
      <div className="currency-settings-header">
        <div className="currency-header-left">
          <HiCurrencyDollar className="currency-header-icon" />
          <h1 className="currency-main-title">Currency Settings</h1>
        </div>
        <button
          className="currency-refresh-btn"
          onClick={handleRefresh}
          disabled={!initialized || isSaving || contextLoading}
        >
          <FaSync className="currency-refresh-icon" />
          Refresh Rates
        </button>
      </div>

      {/* Shop + its base currency (from the shop's country, cannot be changed) */}
      <div className="currency-shop-info">
        <h2>
          {currentShopName}
          {currentShopCountry && (
            <span style={{ fontSize: '14px', fontWeight: 'normal', color: '#666', marginLeft: '10px' }}>
              ({currentShopCountry})
            </span>
          )}
          <span
            style={{
              fontSize: '18px',
              marginLeft: '12px',
              color: '#004aad',
              fontWeight: 'bold',
              background: '#e8f0fe',
              padding: '2px 12px',
              borderRadius: '20px',
              display: 'inline-block',
            }}
          >
            {shopBaseSymbol} {shopBaseCurrency}
          </span>
        </h2>
        <div style={{ fontSize: '14px', color: '#666', marginTop: '6px' }}>
          Base currency{currentShopCountry ? ` (${currentShopCountry})` : ''}:{' '}
          <strong>
            {shopBaseName} ({shopBaseCurrency})
          </strong>{' '}
          — prices are saved in this currency.
        </div>
        <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
          Showing prices in:{' '}
          <strong>
            {displayName} ({displayCurrency})
          </strong>
          <span style={{ marginLeft: '8px', fontSize: '18px', color: '#004aad' }}>{displaySymbol}</span>
          {isShowingBase && <span style={{ marginLeft: '8px', color: '#888' }}>(same as base)</span>}
        </div>
      </div>

      {(error || contextError) && (
        <div className="currency-error-message">
          <span className="currency-error-icon">❌</span>
          {error || contextError}
          <button
            className="currency-error-close"
            onClick={() => {
              setError(null);
              clearError();
            }}
          >
            ×
          </button>
        </div>
      )}

      <div className="currency-cards-grid">
        <div className="currency-settings-card">
          <h2 className="currency-card-title">Display Currency</h2>
          <p className="currency-card-description">
            Choose the currency in which <strong>{currentShopName}</strong> prices are shown.
            Only currencies of countries that have a shop are listed. Prices are converted from{' '}
            <strong>{shopBaseCurrency}</strong> with live exchange rates.
          </p>

          <div className="currency-card-content">
            <div className="currency-select-label">Select Currency:</div>
            <div className="currency-custom-dropdown">
              <div
                className="currency-dropdown-selector"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              >
                <div className="currency-selected-option">
                  <span className="currency-symbol" style={{ fontSize: '1.4rem' }}>
                    {selectedCurrency.symbol}
                  </span>
                  <span className="currency-name">{selectedCurrency.name}</span>
                  <span className="currency-code">({selectedCurrency.code})</span>
                </div>
                <span className="currency-dropdown-arrow">▼</span>
              </div>

              {isDropdownOpen && (
                <div className="currency-dropdown-menu show">
                  {currencies.map((item) => (
                    <div
                      key={item.code}
                      className="currency-dropdown-item"
                      onClick={() => handleSelect(item)}
                    >
                      <span className="currency-symbol" style={{ fontSize: '1.3rem' }}>
                        {item.symbol}
                      </span>
                      <span className="currency-name">{item.name}</span>
                      <span className="currency-code">({item.code})</span>
                      {item.countries && item.countries.length > 0 && (
                        <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#888' }}>
                          {item.countries.join(', ')}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="currency-current-selection-box">
              <div className="currency-current-selection-label">
                {hasChanges ? 'Selected (not saved yet):' : 'Current Selection:'}
              </div>
              <div
                className="currency-current-selection-value"
                style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
              >
                <span style={{ fontSize: '1.8rem', color: '#004aad' }}>{selectedCurrency.symbol}</span>
                {selectedCurrency.name} ({selectedCurrency.code})
              </div>

              {selectedCurrency.code === shopBaseCurrency ? (
                <div style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
                  This is the shop's own currency, so no conversion is needed.
                </div>
              ) : rateKnown ? (
                <>
                  <div style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
                    <strong>Exchange Rate:</strong> 1 {shopBaseCurrency} = {selectedRate.toFixed(4)}{' '}
                    {selectedCurrency.code}
                  </div>
                  <div style={{ marginTop: '5px', fontSize: '12px', color: '#999' }}>
                    <em>
                      Example: {shopBaseSymbol}10.00 = {selectedCurrency.symbol}
                      {(10 * selectedRate).toFixed(2)}
                    </em>
                  </div>
                </>
              ) : (
                <div style={{ marginTop: '10px', fontSize: '14px', color: '#c0392b' }}>
                  The exchange rate for {shopBaseCurrency} → {selectedCurrency.code} is not available
                  right now. Press "Refresh Rates" and try again.
                </div>
              )}

              {updatedAt && (
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#999' }}>
                  Rates updated: {updatedAt}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="currency-action-buttons">
        <button
          className="currency-reset-btn"
          onClick={resetToDefault}
          disabled={!initialized || isSaving || contextLoading || isShowingBase}
        >
          {`Reset to ${shopBaseCurrency}`}
        </button>
        <button
          className="currency-save-btn"
          onClick={saveSettings}
          disabled={!initialized || isSaving || contextLoading || !hasChanges || !rateKnown}
        >
          {isSaving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
};

export default CurrencysettingTabs;